import { useEffect, useRef, useState } from 'react';

// How long "Saved just now" stays visible before it fades out.
const SAVED_VISIBLE_MS = 2500;

/**
 * Drives the transient in-flight save indicator on the pedigree canvas.
 *
 * The source of truth is `isSaving`, the render-visible save-state flag
 * (pedigreeState.save === IN_PROGRESS). This hook must reuse that flag; it must
 * not introduce a second notion of "saving". (Note: the add-relative throttle
 * keys off a separate 400ms click-debounce latch, NOT this flag, so the two are
 * deliberately not unified.)
 *
 * Lifecycle:
 *   idle   (isSaving true)  -> saving
 *   saving (isSaving false) -> saved (held for SAVED_VISIBLE_MS)
 *   saved  (timer elapses)  -> idle  (indicator fades out and unmounts)
 *   saved  (isSaving true)  -> saving (hide timer cancelled)
 *
 * The "You are editing" chip is a separate, persistent element and is NOT
 * driven by this hook.
 *
 * EXTENSION POINT (open question 1): a failed-save state is intentionally not
 * implemented here. If a save-failure flag is added later, extend this hook to
 * return an 'error' state; do NOT collapse a failed save into 'saved' or 'idle'.
 *
 * @param {boolean} isSaving whether a save is currently in flight
 * @returns {'idle'|'saving'|'saved'} the indicator state
 */
export function usePedigreeSaveIndicator(isSaving) {
  const [state, setState] = useState('idle');
  const hideTimer = useRef(null);
  const wasSaving = useRef(false);

  useEffect(() => {
    if (isSaving) {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      wasSaving.current = true;
      setState('saving');
      return;
    }

    // isSaving is false. Only move to 'saved' if we were genuinely saving, so a
    // first render with isSaving=false stays 'idle' rather than flashing "Saved".
    if (wasSaving.current) {
      wasSaving.current = false;
      setState('saved');
      hideTimer.current = setTimeout(() => {
        setState('idle');
        hideTimer.current = null;
      }, SAVED_VISIBLE_MS);
    }
  }, [isSaving]);

  // Clear any pending timer on unmount.
  useEffect(
    () => () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    },
    []
  );

  return state;
}

export { SAVED_VISIBLE_MS };
